// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/header/HeaderDesktop.tsx
// Version: v1.0.2
// Description: Desktop Header - "Login" statt "LOGIN"
import { User } from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../lang/index.lang.tsx";
import LanguageSwitcher from "../ui/LanguageSwitcher.tsx";
import SubmenuDesktop from "./SubmenuDesktop.tsx";
import type { NavigationItem, DropdownType } from "./navigationConfig.tsx";

// ==========================================
// ZENTRALE KONSTANTEN - SINGLE SOURCE OF TRUTH
// ==========================================
const HEADER_HEIGHT = 50; // px - Haupthöhe des Headers
const SUBMENU_TOP_SPACING = 0; // px - Abstand zum Submenu
const SUBMENU_TOP_OFFSET = HEADER_HEIGHT + SUBMENU_TOP_SPACING; // 50px
const NAV_UNDERLINE_HEIGHT = 3; // px
const LOGO_WIDTH = 100; // px
const FONT_SIZE_DROPDOWN = 15; // px
const FONT_SIZE_REGULAR = 15; // px
const FONT_SIZE_LOGIN = 15; // px
const ICON_SIZE = 24; // px (w-6 h-6 = 24px)
const TRANSITION_SUBMENU = 50; // ms
const TRANSITION_HEADER_HIDE = 300; // ms - Header ein-/ausblenden
const SCROLL_THRESHOLD = 10; // px - Mindest-Scroll für Hide/Show
const SCROLL_DELTA_MIN = 5; // px - Minimale Scroll-Änderung

// ==========================================
// STYLE HELPERS
// ==========================================
const getHeightStyle = () => ({ height: `${HEADER_HEIGHT}px` });
const getHeaderStyle = (visible: boolean): React.CSSProperties => ({
  height: `${HEADER_HEIGHT}px`,
  transform: visible ? 'translateY(0)' : `translateY(-${HEADER_HEIGHT}px)`,
  transition: `transform ${TRANSITION_HEADER_HIDE}ms ease-in-out`
});
const getLogoStyle = () => ({ 
  width: `${LOGO_WIDTH}px`, 
  maxHeight: `${HEADER_HEIGHT}px` 
});
const getIconStyle = () => ({ 
  width: `${ICON_SIZE}px`, 
  height: `${ICON_SIZE}px` 
});

// ==========================================
// INTERFACES
// ==========================================
interface SubmenuDesktopRef {
  forceClosePanel: () => Promise<void>;
}

interface HeaderDesktopProps {
  navigationConfig: NavigationItem[];
}

// ==========================================
// COMPONENT
// ==========================================
const HeaderDesktop: React.FC<HeaderDesktopProps> = ({ navigationConfig }) => {
  const location = useLocation();
  const { t } = useLanguage();

  // State Management
  const [activeDropdown, setActiveDropdown] = React.useState<DropdownType | null>(null);
  const [panelMounted, setPanelMounted] = React.useState(false);
  const [lastType, setLastType] = React.useState<DropdownType | null>(null);
  const [pendingType, setPendingType] = React.useState<DropdownType | null>(null);
  const submenuRef = React.useRef<SubmenuDesktopRef>(null);

  // Scroll-State für Header Hide/Show
  const [headerVisible, setHeaderVisible] = React.useState(true);
  const lastScrollY = React.useRef(0);
  const scrollDirection = React.useRef<'up' | 'down' | null>(null);

  // Effects
  React.useEffect(() => {
    if (activeDropdown) {
      setPanelMounted(true);
      setLastType(activeDropdown);
    }
  }, [activeDropdown]);

  // Scroll-Listener für Header Hide/Show (NUR auf Nicht-Home-Seiten)
  React.useEffect(() => {
    const isHomePage = location.pathname === '/';
    
    // Auf Home-Seite: Header immer sichtbar
    if (isHomePage) {
      setHeaderVisible(true);
      return;
    }

    // Auf anderen Seiten: Scroll-Verhalten aktivieren
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      // Nur reagieren wenn genug gescrollt wurde
      if (Math.abs(delta) < SCROLL_DELTA_MIN) {
        return;
      }

      // Scroll-Richtung bestimmen
      const isScrollingDown = delta > 0;
      const isScrollingUp = delta < 0;

      // Header ausblenden wenn runter gescrollt wird (und nicht ganz oben)
      if (isScrollingDown && currentScrollY > SCROLL_THRESHOLD) {
        if (scrollDirection.current !== 'down') {
          scrollDirection.current = 'down';
          setHeaderVisible(false);
          // Schließe offene Dropdowns beim Scrollen
          if (activeDropdown) {
            setActiveDropdown(null);
          }
        }
      }

      // Header einblenden wenn hoch gescrollt wird
      if (isScrollingUp) {
        if (scrollDirection.current !== 'up') {
          scrollDirection.current = 'up';
          setHeaderVisible(true);
        }
      }

      // Ganz oben? Immer anzeigen
      if (currentScrollY <= SCROLL_THRESHOLD) {
        setHeaderVisible(true);
        scrollDirection.current = null;
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeDropdown, location.pathname]);

  // Event Handlers
  const handleTransitionEnd = () => {
    if (!activeDropdown) {
      if (pendingType) {
        setLastType(pendingType);
        setPendingType(null);
        setPanelMounted(true);
        setActiveDropdown(pendingType);
        return;
      }
      setPanelMounted(false);
    }
  };

  const onDropdownButtonClick = async (targetType: DropdownType) => {
    const current = activeDropdown;

    if (current === targetType) {
      if (submenuRef.current) {
        await submenuRef.current.forceClosePanel();
      }
      setPendingType(null);
      setActiveDropdown(null);
      return;
    }

    if (current) {
      if (submenuRef.current) {
        await submenuRef.current.forceClosePanel();
      }
      setPendingType(targetType);
      setActiveDropdown(null);
      return;
    }

    setLastType(targetType);
    setActiveDropdown(targetType);
  };

  const handleMenuClose = async () => {
    if (submenuRef.current) {
      await submenuRef.current.forceClosePanel();
    }
    setActiveDropdown(null);
  };

  // Helper Functions
  const isActivePath = (path: string) => location.pathname === path;
  const isDropdownOpen = (type?: string) => !!type && activeDropdown === type;

  const getContainerClasses = (href: string, dropdownType?: string) => {
    const active = isActivePath(href) || isDropdownOpen(dropdownType);
    return [
      "group relative flex items-center px-4 font-medium transition-colors duration-200",
      active ? "text-white" : "text-gray-300 hover:text-white"
    ].join(" ");
  };

  const getLabelClasses = (href: string, dropdownType?: string) => {
    const pathActive = location.pathname === href;
    const open = !!dropdownType && activeDropdown === dropdownType;

    return [
      "relative inline-flex items-center",
      "after:absolute after:left-0 after:right-0 after:bottom-0",
      "after:bg-[#37acdc] after:transition-opacity after:duration-200",
      open || pathActive
        ? "after:opacity-100"
        : "after:opacity-0 group-hover:after:opacity-100"
    ].join(" ");
  };

  const getLabelStyle = (): React.CSSProperties => ({
    height: `${HEADER_HEIGHT}px`,
  });

  const shouldRenderContent = !!activeDropdown || panelMounted;

  return (
    <>
      {/* HEADER */}
      <header 
        className="bg-black text-white sticky top-0 z-50"
        style={getHeaderStyle(headerVisible)}
      >
        <div className="max-w-[1510px] mx-auto h-full">
          <div className="flex justify-between items-center h-full px-4 sm:px-6 lg:px-8">
            
            {/* LOGO */}
            <Link
              to="/"
              className="flex items-center"
              onClick={() => setActiveDropdown(null)}
              aria-label={t("common.home")}
            >
              <img
                src="/images/ESTATA-Logo-150.png"
                alt="ESTATA Logo"
                className="object-contain"
                style={getLogoStyle()}
              />
            </Link>

            {/* NAVIGATION */}
            <nav className="flex items-center h-full">
              {navigationConfig.map((item) => {
                const containerCls = getContainerClasses(item.href, item.dropdownType);
                const labelCls = getLabelClasses(item.href, item.dropdownType);
                const hasDropdown = item.hasDropdown && !!item.dropdownType;
                const fontSize = hasDropdown ? FONT_SIZE_DROPDOWN : FONT_SIZE_REGULAR;

                const LabelContent = (
                  <span className={labelCls} style={getLabelStyle()}>
                    <span className="flex items-center" style={{ fontSize: `${fontSize}px` }}>
                      <span>{item.name}</span>
                    </span>
                    <style>{`
                      .${labelCls.split(' ')[0]}::after {
                        height: ${NAV_UNDERLINE_HEIGHT}px;
                      }
                    `}</style>
                  </span>
                );

                return (
                  <div key={item.name} className="relative h-full flex items-center">
                    {hasDropdown ? (
                      <button
                        onClick={() => onDropdownButtonClick(item.dropdownType as DropdownType)}
                        className={containerCls}
                        style={getHeightStyle()}
                        aria-expanded={isDropdownOpen(item.dropdownType)}
                        aria-haspopup="true"
                      >
                        {LabelContent}
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setActiveDropdown(null)}
                        className={containerCls}
                        style={getHeightStyle()}
                        aria-label={item.name}
                      >
                        {LabelContent}
                      </Link>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* RECHTE SEITE */}
            <div className="flex items-center space-x-4 h-full">
              <Link 
                to="/login"
                className="text-white transition-colors flex items-center space-x-2 p-2 mr-2"
                onClick={() => setActiveDropdown(null)}
                aria-label={t("navigation.admin")}
              >
                <User style={getIconStyle()} />
                <span style={{ fontSize: `${FONT_SIZE_LOGIN}px` }} className="font-medium">
                  Login
                </span>
              </Link>
              <LanguageSwitcher 
                variant="desktop" 
                onLanguageChange={() => {}} 
                className="pr-0" 
              />
            </div>
          </div>
        </div>
      </header>

      {/* BACKDROP */}
      {panelMounted && (
        <button
          aria-label="Schließen (Backdrop)"
          onClick={handleMenuClose}
          className={[
            "fixed inset-0 z-30 bg-black/30 backdrop-blur-sm cursor-default focus:outline-none",
            activeDropdown ? "opacity-100" : "opacity-0 pointer-events-none"
          ].join(" ")}
          style={{ transitionDuration: `${TRANSITION_SUBMENU}ms` }}
        />
      )}

      {/* SUBMENU */}
      <div
        onTransitionEnd={handleTransitionEnd}
        className={[
          "fixed left-0 right-0 z-40 bg-black will-change-transform transition-transform duration-500 ease-in-out",
          activeDropdown ? "translate-y-0 pointer-events-auto" : "-translate-y-full pointer-events-none"
        ].join(" ")}
        style={{ top: `${SUBMENU_TOP_OFFSET}px` }}
      >
        {shouldRenderContent && lastType && (
          <SubmenuDesktop
            ref={submenuRef}
            type={lastType}
            isOpen={activeDropdown === lastType}
            onClose={handleMenuClose}
          />
        )}
      </div>
    </>
  );
};

export default HeaderDesktop;